import qs from 'qs';

const SNAPSHOT_MEDIA_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'pdf', 'webp', 'gif', 'bmp', 'tif', 'tiff',
]);

const EXPECTED_SIDES = {
  1: [
    { side: 'front', label: 'CCCD Front' },
    { side: 'back', label: 'CCCD Back' },
  ],
  2: [{ side: 'front', label: 'Passport' }],
  3: [{ side: 'front', label: 'Enterprise Registration' }],
};

const nonBlank = value => value != null && String(value).trim() !== '';

const toID = value => (nonBlank(value) ? String(value) : '');

const purchaserName = (customer = {}) => {
  const contact = customer.contact || {};
  return contact.preferred_name || contact.name || customer.preferred_name || customer.name || 'Purchaser';
};

const safeFileName = (media, label) => {
  const candidate = media && (media.name || media.label);
  if (!nonBlank(candidate)) return label;
  const basename = String(candidate).split(/[\\/]/).pop().trim();
  return basename || label;
};

export const snapshotIsReady = opportunity =>
  !!(opportunity && nonBlank(opportunity.snapshot_frozen_at));

export const expectedSnapshotPurchaserIDs = (opportunity = {}) => {
  const ids = [
    opportunity.primary_customer_id,
    opportunity.primary_customer && opportunity.primary_customer.id,
  ]
    .concat(Array.isArray(opportunity.customer_ids) ? opportunity.customer_ids : [])
    .filter(nonBlank)
    .map(toID);
  return Array.from(new Set(ids));
};

const expectedCustomers = (opportunity = {}) => {
  const customers = [];
  if (opportunity.primary_customer) customers.push(opportunity.primary_customer);
  if (Array.isArray(opportunity.customers)) customers.push(...opportunity.customers);
  const byID = new Map();
  customers.forEach((customer) => {
    if (customer && nonBlank(customer.id)) byID.set(toID(customer.id), customer);
  });
  return byID;
};

export const isScopedSnapshotMediaURL = (
  link, opportunityID, customerID, snapshotContactID, side, apiOrigin,
) => {
  if (!nonBlank(link) ||
    !nonBlank(opportunityID) ||
    !nonBlank(customerID) ||
    !nonBlank(snapshotContactID)) return false;
  try {
    // Links arrive from Book API. Reject a foreign absolute or protocol-relative URL instead of
    // treating it as an authenticated snapshot-media link merely because its path looks familiar.
    if (String(link).startsWith('//')) return false;
    const baseOrigin = apiOrigin || window.location.origin;
    const approved = document.createElement('a');
    approved.href = baseOrigin;
    const approvedOrigin = `${approved.protocol}//${approved.host}`;
    const parsed = document.createElement('a');
    parsed.href = /^[a-z][a-z0-9+.-]*:/i.test(String(link)) ? link : `${approvedOrigin}/${String(link).replace(/^\//, '')}`;
    const parsedOrigin = `${parsed.protocol}//${parsed.host}`;
    const query = qs.parse(parsed.search.replace(/^\?/, ''));
    if (parsedOrigin !== approvedOrigin) return false;
    const segments = parsed.pathname.split('/').filter(Boolean).map(decodeURIComponent);
    return segments.length === 4 &&
      segments[0] === 'opportunity' &&
      segments[1] === `customer_id_${side}` &&
      segments[2] === toID(customerID) &&
      query.opportunity_id === toID(opportunityID) &&
      query.contact_id === toID(snapshotContactID);
  } catch (e) {
    return false;
  }
};

const extensionOfPath = (value) => {
  if (!nonBlank(value)) return '';
  const basename = String(value).split(/[?#]/)[0].split(/[\\/]/).pop() || '';
  const match = /\.([a-z0-9]+)$/i.exec(basename);
  return match ? match[1].toLowerCase() : '';
};

const mediaExtension = (media) => {
  if (!media) return '';
  const explicit = nonBlank(media.extension) ? String(media.extension).toLowerCase().replace(/^\./, '') : '';
  const candidates = [explicit, extensionOfPath(media.name), extensionOfPath(media.key)];
  return candidates.find(candidate => SNAPSHOT_MEDIA_EXTENSIONS.has(candidate)) || '';
};

const unavailableRow = ({ customerID, name, label, role }) => ({
  id: `snapshot-${customerID}-${role}-unavailable`,
  customerID,
  snapshotDocument: true,
  snapshotUnavailable: true,
  fileName: `${label} — unavailable`,
  fileType: 'identity',
  purchaserName: name,
  snapshotLabel: label,
});

const snapshotRow = ({ opportunity, customerID, customer, ref, side, label, apiOrigin }) => {
  const contact = customer.contact || {};
  const media = contact[`ic_${side}`];
  const link = contact[`ic_${side}_link`];
  const extension = mediaExtension(media);
  const row = {
    customerID,
    name: purchaserName(customer),
    label,
    role: side,
  };
  if (!extension ||
    !isScopedSnapshotMediaURL(
    link, opportunity.id, customerID, ref.snapshot_contact_id, side, apiOrigin,
  )) {
    return unavailableRow(row);
  }
  return {
    id: `snapshot-${customerID}-${side}`,
    customerID,
    snapshotDocument: true,
    snapshotURL: link,
    snapshotExtension: extension,
    fileName: safeFileName(media, label),
    fileType: 'identity',
    purchaserName: row.name,
    snapshotLabel: label,
  };
};

// Snapshot readiness is authoritative. Once frozen, this deliberately emits an unavailable row
// for incomplete refs or a failed snapshot hydration instead of reading the live CRM customer.
export const getSnapshotIdentityDocuments = (opportunity, apiOrigin) => {
  if (!snapshotIsReady(opportunity)) return [];
  const purchasers = expectedSnapshotPurchaserIDs(opportunity);
  const refsByCustomerID = new Map((opportunity.snapshot_contacts || [])
    .filter(ref => ref && nonBlank(ref.customer_id))
    .map(ref => [toID(ref.customer_id), ref]));
  const customersByID = expectedCustomers(opportunity);

  return purchasers.reduce((rows, customerID) => {
    const ref = refsByCustomerID.get(customerID);
    const customer = customersByID.get(customerID);
    const contact = customer && customer.contact;
    const name = purchaserName(customer);
    const snapshotContactID = ref && toID(ref.snapshot_contact_id);
    const hydratedFromSnapshot =
      !!(snapshotContactID && contact && toID(contact.id) === snapshotContactID);
    const sides = hydratedFromSnapshot ? EXPECTED_SIDES[String(contact.identity_type)] : null;

    if (!sides) {
      rows.push(unavailableRow({ customerID, name, label: 'Identity Snapshot', role: 'identity' }));
      return rows;
    }
    sides.forEach(({ side, label }) => rows.push(snapshotRow({
      opportunity, customerID, customer, ref, side, label, apiOrigin,
    })));
    return rows;
  }, []);
};

// Hide only copies carrying the server-reserved provenance marker. Historical filenames
// and generic app sources are ambiguous; retain them rather than hide manual uploads.
export const isGeneratedIdentityMirror = (document = {}) => {
  if (document.fileType !== 'identity' || !nonBlank(document.fileName)) return false;
  const sourceApp = document.sourceApp || document.source_app;
  if (sourceApp !== 'mhub-book-api:identity-mirror') return false;
  return /^identity_[12]_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_[0-9a-f]{1,8}\.(?:jpe?g|png|pdf)$/i.test(document.fileName);
};

export const detectSnapshotMediaType = (bytes) => {
  const value = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (value.length >= 3 && value[0] === 0xFF && value[1] === 0xD8 && value[2] === 0xFF) {
    return { mimeType: 'image/jpeg', extension: 'jpg', previewable: true };
  }
  if (value.length >= 8 &&
    value[0] === 0x89 &&
    value[1] === 0x50 &&
    value[2] === 0x4E &&
    value[3] === 0x47 &&
    value[4] === 0x0D &&
    value[5] === 0x0A &&
    value[6] === 0x1A &&
    value[7] === 0x0A) {
    return { mimeType: 'image/png', extension: 'png', previewable: true };
  }
  if (value.length >= 5 &&
    value[0] === 0x25 &&
    value[1] === 0x50 &&
    value[2] === 0x44 &&
    value[3] === 0x46 &&
    value[4] === 0x2D) {
    return { mimeType: 'application/pdf', extension: 'pdf', previewable: false, pdf: true };
  }
  return null;
};

export const snapshotFileName = (fileName, extension) => {
  const basename = String(fileName || 'Identity Snapshot').split(/[\\/]/).pop() || 'Identity Snapshot';
  const stem = basename.replace(/\.[^.]+$/, '') || 'Identity Snapshot';
  return `${stem}.${extension}`;
};

export const snapshotDownloadOnlyExtension = extension => (
  SNAPSHOT_MEDIA_EXTENSIONS.has(extension) ? extension : ''
);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/snapshotIdentityDocuments.js